/** @jsxImportSource @emotion/react */
import React, { useMemo } from "react";
import { theme } from "antd";
import { css, keyframes } from "@emotion/react";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const counterSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: .92; }
  50% { transform: scale(1.035); opacity: 1; }
`;

const flicker = keyframes`
  0%, 100% { opacity: 1; }
  12% { opacity: .92; }
  18% { opacity: 1; }
  32% { opacity: .88; }
  40% { opacity: 1; }
  62% { opacity: .94; }
  74% { opacity: 1; }
`;

const CoreLogo3D = ({ size = 48, variant = "default" }) => {
  const { token } = theme.useToken();
  const id = React.useId();

  const colors = useMemo(() => {
    const primary = token.colorPrimary;
    const accent = token.colorInfo || token.colorPrimary;
    const neon = token.colorSuccess || "#52c41a";
    const bg = token.colorBgElevated || "#0a0c10";
    return { primary, accent, neon, bg };
  }, [token]);

  const isNucleus = variant === "nucleus";
  const isOrbit = variant === "orbit";
  const isPremium = variant === "premium";
  const isCompact = variant === "compact";

  const glowOpacity = isNucleus ? 0.8 : isOrbit ? 0.7 : isPremium ? 0.7 : 0.6;
  const ringOpacity = isOrbit ? 0.9 : isNucleus ? 0.55 : isPremium ? 0.6 : 0.65;
  const dashA = isOrbit
    ? "1.4 4"
    : isNucleus
      ? "9 14"
      : isPremium
        ? "10 6"
        : "8 7";
  const outerRingWidth = isOrbit
    ? 1.6
    : isNucleus
      ? 1.1
      : isPremium
        ? 1.4
        : 1.3;
  const innerRingWidth = isOrbit ? 1.4 : isNucleus ? 0.9 : isPremium ? 1.2 : 1;
  const coreRadius = isNucleus ? 17 : isOrbit ? 9 : isPremium ? 15 : 13.5;

  const wrapCss = css({
    width: size,
    height: size,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    transform: "translateZ(0)",
    "& svg": {
      display: "block",
    },
    "@media (prefers-reduced-motion: reduce)": {
      "& [data-anim='spin'], & [data-anim='counter'], & [data-anim='pulse'], & [data-anim='flicker']":
        {
          animation: "none !important",
        },
    },
  });

  const orbitCss = css({
    transformOrigin: "50% 50%",
    animation: `${spin} ${
      isOrbit ? 9 : isNucleus ? 14 : isPremium ? 10 : isCompact ? 11 : 12
    }s linear infinite`,
  });

  const orbit2Css = css({
    transformOrigin: "50% 50%",
    animation: `${counterSpin} ${
      isOrbit ? 6.5 : isNucleus ? 16 : isPremium ? 7.5 : isCompact ? 8.5 : 9.5
    }s linear infinite`,
  });

  const pulseCss = css({
    transformOrigin: "50% 50%",
    animation: `${pulse} ${
      isNucleus ? 1.8 : isOrbit ? 3.4 : isPremium ? 2.2 : 3.0
    }s ease-in-out infinite`,
  });

  const flickerCss = css({
    animation: `${flicker} ${isPremium ? 6 : 8}s linear infinite`,
  });

  const stars = useMemo(() => {
    return [
      { x: 12, y: 18, r: 0.9, o: 0.7 },
      { x: 86, y: 22, r: 0.8, o: 0.65 },
      { x: 18, y: 78, r: 0.8, o: 0.6 },
      { x: 90, y: 70, r: 1.0, o: 0.75 },
      { x: 8, y: 52, r: 0.7, o: 0.55 },
      { x: 70, y: 8, r: 0.7, o: 0.6 },
      { x: 54, y: 92, r: 0.9, o: 0.7 },
    ];
  }, []);

  return (
    <span css={wrapCss} aria-hidden="true">
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        role="img"
        focusable="false"
      >
        <defs>
          <radialGradient id={`${id}-core`} cx="30%" cy="28%" r="70%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
            <stop offset="35%" stopColor={colors.accent} stopOpacity="0.9" />
            <stop offset="62%" stopColor={colors.primary} stopOpacity="0.75" />
            <stop offset="100%" stopColor={colors.bg} stopOpacity="0.15" />
          </radialGradient>

          <linearGradient id={`${id}-ring`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.neon} stopOpacity="0.9" />
            <stop offset="45%" stopColor={colors.accent} stopOpacity="0.85" />
            <stop offset="100%" stopColor={colors.primary} stopOpacity="0.9" />
          </linearGradient>

          <linearGradient
            id={`${id}-stroke`}
            x1="0%"
            y1="100%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor={colors.primary} stopOpacity="0.7" />
            <stop offset="50%" stopColor={colors.accent} stopOpacity="0.8" />
            <stop offset="100%" stopColor={colors.neon} stopOpacity="0.65" />
          </linearGradient>

          <filter
            id={`${id}-glow`}
            x="-40%"
            y="-40%"
            width="180%"
            height="180%"
          >
            <feGaussianBlur
              stdDeviation={isPremium ? 2.6 : 2.1}
              result="blur"
            />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 12 -6
              "
              result="glow"
            />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Starfield background */}
        <g css={flickerCss} data-anim="flicker">
          {stars.map((s, i) => (
            <circle
              key={`star-${i}`}
              cx={s.x}
              cy={s.y}
              r={s.r}
              fill={colors.bg}
              stroke={colors.neon}
              strokeOpacity={s.o * 0.5}
              filter={`url(#${id}-glow)`}
            />
          ))}
        </g>

        <g css={flickerCss} data-anim="flicker">
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke={`url(#${id}-ring)`}
            strokeOpacity={ringOpacity}
            strokeWidth={outerRingWidth}
            strokeDasharray={dashA}
          />

          <g css={orbitCss} data-anim="spin">
            <circle
              cx="50"
              cy="50"
              r={isOrbit ? 38 : isNucleus ? 30 : 34}
              fill="none"
              stroke={`url(#${id}-stroke)`}
              strokeOpacity={isOrbit ? 0.65 : 0.5}
              strokeWidth={isOrbit ? 1.8 : isNucleus ? 1.1 : 1.2}
              strokeDasharray={isOrbit ? "2 4" : isPremium ? "3 6" : "2 7"}
            />
            <path
              d="M 50 16 A 34 34 0 0 1 84 50"
              fill="none"
              stroke={colors.accent}
              strokeOpacity={isOrbit ? 0.45 : 0.35}
              strokeWidth={isOrbit ? 2.2 : 2}
              strokeLinecap="round"
              filter={`url(#${id}-glow)`}
            />
            <circle
              cx="84"
              cy="50"
              r={isOrbit ? 2.6 : 2.2}
              fill={colors.neon}
              opacity={glowOpacity}
              filter={`url(#${id}-glow)`}
            />
          </g>

          <g css={orbit2Css} data-anim="counter">
            <circle
              cx="50"
              cy="50"
              r={isOrbit ? 30 : isNucleus ? 22 : 26}
              fill="none"
              stroke={`url(#${id}-stroke)`}
              strokeOpacity={isOrbit ? 0.6 : 0.45}
              strokeWidth={innerRingWidth}
              strokeDasharray={isOrbit ? "5 4" : isPremium ? "8 5" : "7 6"}
            />
            <circle
              cx="26"
              cy="50"
              r="1.8"
              fill={colors.primary}
              opacity={glowOpacity}
              filter={`url(#${id}-glow)`}
            />
            <circle
              cx="50"
              cy="24"
              r={isOrbit ? 1.6 : 1.4}
              fill={colors.accent}
              opacity={glowOpacity * 0.85}
              filter={`url(#${id}-glow)`}
            />
          </g>

          <g css={pulseCss} data-anim="pulse">
            <circle
              cx="50"
              cy="50"
              r={coreRadius}
              fill={`url(#${id}-core)`}
              filter={`url(#${id}-glow)`}
            />
            <polygon
              points="50,30 56,44 72,50 56,56 50,70 44,56 28,50 44,44"
              fill="none"
              stroke={`url(#${id}-ring)`}
              strokeOpacity={
                isOrbit ? 0.85 : isNucleus ? 0.45 : isPremium ? 0.5 : 0.55
              }
              strokeWidth={
                isOrbit ? 1.8 : isNucleus ? 1 : isPremium ? 1.2 : 1.2
              }
              filter={`url(#${id}-glow)`}
            />
            <polygon
              points="50,34 54,46 66,50 54,54 50,66 46,54 34,50 46,46"
              fill="none"
              stroke={colors.accent}
              strokeOpacity={isNucleus ? 0.55 : isPremium ? 0.45 : 0.35}
              strokeWidth="1"
            />
          </g>

          <g opacity={isPremium ? 0.55 : 0.42}>
            <path
              d="M18 62 C30 56, 40 74, 54 66 C68 58, 74 40, 86 44"
              fill="none"
              stroke={colors.primary}
              strokeOpacity="0.35"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <path
              d="M16 38 C30 44, 44 24, 58 34 C72 44, 74 70, 86 64"
              fill="none"
              stroke={colors.neon}
              strokeOpacity="0.22"
              strokeWidth="1"
              strokeLinecap="round"
            />
          </g>

          <g
            filter={isPremium ? `url(#${id}-glow)` : undefined}
            opacity={isPremium ? 0.55 : 0.4}
          >
            <circle cx="20" cy="48" r="0.9" fill={colors.accent} />
            <circle cx="30" cy="70" r="0.8" fill={colors.primary} />
            <circle cx="74" cy="30" r="0.9" fill={colors.neon} />
            <circle cx="78" cy="66" r="0.8" fill={colors.accent} />
            <circle cx="62" cy="18" r="0.7" fill={colors.primary} />
          </g>
        </g>
      </svg>
    </span>
  );
};

export default CoreLogo3D;
